import { apiErrorSchema, type ApiError, type ErrorCode } from '@complan/contracts';

/**
 * A thrown API failure carrying one of the six error shapes of the design's
 * taxonomy (`unauthenticated`, `forbidden`, `not_found`, `validation_failed`,
 * `conflict`, `internal`).
 *
 * The error boundary and the query layer branch on `body.code`, so the mapping
 * from an HTTP response to a rendered message lives in exactly one place. No shape
 * carries Initiative content beyond the version-conflict diff the contracts
 * package already scopes to a writer (see `errors.ts`).
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly body: ApiError;

  constructor(status: number, body: ApiError) {
    super(`API request failed (${body.code})`);
    this.name = 'ApiRequestError';
    this.status = status;
    this.body = body;
  }

  get code(): ErrorCode {
    return this.body.code;
  }
}

/**
 * Coerce an unknown response body into an {@link ApiError}. A body that does not
 * match any of the six shapes is treated as `internal`: an unexpected wire shape
 * is a defect, not a user-recoverable state.
 */
export function toApiError(status: number, body: unknown, correlationId: string): ApiError {
  const parsed = apiErrorSchema.safeParse(body);
  if (parsed.success) {
    return parsed.data;
  }
  return { code: 'internal', correlationId };
}
